import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Grid, Paper, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import update from 'immutability-helper';
import TimePeriodSelector from '../acquisition/TimePeriodSelector';
import DateInput from '../acquisition/DateInput';
import CloudSelector from '../acquisition/CloudSelector';
import StepperButtons from '../acquisition/StepperButtons';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import ImageTable from '../visualization/ImageTable';
import { getImageKey } from '../visualization/imageCsv';
import ImportDatesButton from '../acquisition/ImportDatesButton';
import DateRangeError from './DateRangeError';
import { FINALIZE } from '../../../hooks/useStepNavigations';
import { datesBetween, formatDateDiff, formatLocaleDate } from '../../../common/utils';
import { getModelDateLimits, getDateRangeErrors, DATE_LIMIT_MONTHS } from '../../../algorithms/shoreS';
import { Actions as ShoreS } from '../../../store/ducks/shoreS';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: theme.spacing(2),
  },
  description: {
    margin: theme.spacing(4, 0, 2),
  },
  dateInputs: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 640,
    marginBottom: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  empty: {
    margin: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingHint: {
    marginTop: theme.spacing(1),
    color: '#7952B3',
    textAlign: 'center',
  },
  section: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
  },
  placeholder: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

/**
 * @function toDay
 * @description Trims an ISO timestamp down to its `YYYY-MM-DD` day, the granularity the period
 * filter works at.
 * @param {string} iso - An ISO timestamp, possibly empty.
 * **Called by:
 * @see {ShoreSPeriodImageChooser}
 * @see {onChange}
 * @memberof module:ShoreS
 * @returns {string} The day, or an empty string.
 */
const toDay = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/**
 * @function addDay
 * @description Advances an ISO day by one. Earth Engine's `filterDate` is end-exclusive, so the
 * requested end date has to be pushed out a day to actually be included.
 * @param {string} isoDay - A `YYYY-MM-DD` day.
 * **Called by:
 * @see {ShoreSPeriodImageChooser} - when building the availability request.
 * @memberof module:ShoreS
 * @returns {string} The following day.
 */
const addDay = (isoDay) => {
  if (!isoDay) return isoDay;
  const d = new Date(isoDay);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * @function ShoreSPeriodImageChooser
 * @description Merges Shore-S's period/cloud search and the resulting image-selection table
 * into a single step, so the user can adjust the period or cloud filter and see the candidate
 * list update immediately, without navigating to a separate step to see it and back again to
 * change the dates.
 * @param {Object} props - Component props.
 * @param {Function} props.navigate - Function to navigate between steps.
 * **Called by:
 * @see {ShoresSAcquisitionPage} - Shore-S's acquisition
 * @memberof module:ShoreS
 * @returns {JSX.Element} The ShoreSPeriodImageChooser component.
 */
const ShoreSPeriodImageChooser = ({ navigate }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const availableDates = useSelector((state) => state.shoreS.availableDates, shallowEqual);
  const loading = useSelector((state) => state.shoreS.loading);
  const error = useSelector((state) => state.shoreS.error);
  const metadata = useSelector((state) => state.shoreS.metadata);

  const modelDateRange = useSelector((state) => state.shoreSModel.dateRange, shallowEqual);
  const dateLimits = useMemo(
    () =>
      getModelDateLimits(
        modelDateRange || {
          start: sessionStorage.getItem('shoreSModelDateStart') || '',
          end: sessionStorage.getItem('shoreSModelDateEnd') || '',
        },
      ),
    [modelDateRange],
  );

  const detectedStart = toDay(sessionStorage.getItem('shoreSDateStart') || '');
  const detectedEnd = toDay(sessionStorage.getItem('shoreSDateEnd') || '');


  const [searchRange, setSearchRange] = useState({ start: detectedStart, end: detectedEnd });

  const [period, setPeriod] = useState({ start: detectedStart, end: detectedEnd });
  const [cloudCoverage, setCloudCoverage] = useState(0.05); // 5%

  // True during the initial 400ms debounce window — prevents the empty-state
  // message from flashing before the first EE request has even been dispatched.
  const [pendingFetch, setPendingFetch] = useState(!!(detectedStart && detectedEnd));

  const [selected, setSelected] = useState({});
  const narrowedSignatureRef = useRef(null);

  // Seed the cache with the window detected from the model, once. Every later fetch goes
  // through handleSearch. Debounced only to give the step's mount a beat to settle.
  useEffect(() => {
    if (!detectedStart || !detectedEnd) return undefined;
    const handle = setTimeout(() => {
      setPendingFetch(false);
      dispatch(ShoreS.loadImagesInRange(detectedStart, addDay(detectedEnd)));
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const searchErrors = useMemo(
    () => getDateRangeErrors(searchRange.start, searchRange.end, dateLimits),
    [searchRange.start, searchRange.end, dateLimits],
  );
  const canSearch =
    searchRange.start !== '' && searchRange.end !== '' && !searchErrors.start && !searchErrors.end;


  const handleSearch = () => {
    if (!canSearch) return;
    setPendingFetch(false);
    setPeriod({ ...searchRange });
    sessionStorage.setItem('shoreSDateStart', searchRange.start);
    sessionStorage.setItem('shoreSDateEnd', searchRange.end);
    dispatch(ShoreS.loadImagesInRange(searchRange.start, addDay(searchRange.end)));
  };

  /**
   * @function onChange
   * @description Narrows the visible period from the slider, collapsing it to a single day if
   * the user drags the end before the start. Purely local — the slider can't leave the range
   * already fetched, so this never needs a new Earth Engine request.
   * @param {string} newStart - New start date.
   * @param {string} newEnd - New end date.
   * **Called by:
   * @see {ShoreSPeriodImageChooser} - via the period slider's onChange.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const onChange = (newStart, newEnd) => {
    const start = toDay(newStart);
    const end = toDay(newEnd);
    if (new Date(end) < new Date(start)) {
      setPeriod({ start, end: start });
      return;
    }
    setPeriod({ start, end });
  };

  const filtered = useMemo(
    () => availableDates.filter((entry) => entry.content <= cloudCoverage),
    [availableDates, cloudCoverage],
  );

  const narrowedDates = useMemo(() => {
    if (!period.start || !period.end) return [];
    return filtered.filter((entry) => entry.date >= period.start && entry.date <= period.end);
  }, [filtered, period.start, period.end]);

  const imageCount = period.start && period.end
    ? datesBetween(filtered.map((e) => e.date), period.start, period.end).length
    : 0;

  const visibleDateValues = filtered.map((entry) => entry.date).sort();
  const clampToVisible = (date) => {
    if (!visibleDateValues.length || !date) return date;
    if (date < visibleDateValues[0]) return visibleDateValues[0];
    if (date > visibleDateValues[visibleDateValues.length - 1]) return visibleDateValues[visibleDateValues.length - 1];
    return date;
  };

  // Reinitializes the checkbox selection whenever the narrowed candidate list actually changes
  // (period/cloud filter revealing a different set) — restoring a previously-saved selection
  // when the exact same keys reappear. Images here already passed period + cloud-coverage
  // filtering, so new ones default to checked, same as the original two-step flow. Skips
  // entirely while narrowedDates is empty (e.g. a transient render before period/cloud settle)
  // so a real, non-empty selection restored from sessionStorage is never wiped out.
  useEffect(() => {
    if (!narrowedDates.length) return;
    const signature = narrowedDates.map(getImageKey).join('|');
    if (signature === narrowedSignatureRef.current) return;
    narrowedSignatureRef.current = signature;
    let saved = {};
    try {
      saved = JSON.parse(window.sessionStorage.getItem('shoreSImageSelection') || '{}');
    } catch {
      saved = {};
    }
    const initial = {};
    narrowedDates.forEach((img) => {
      const key = getImageKey(img);
      initial[key] = saved[key] ?? true;
    });
    setSelected(initial);
  }, [narrowedDates]);

  useEffect(() => {
    window.sessionStorage.setItem('shoreSImageSelection', JSON.stringify(selected));
  }, [selected]);

  // Debounced so dragging the period slider doesn't fire a thumbnail request on every
  // intermediate tick — only once the narrowed list settles.
  useEffect(() => {
    if (!narrowedDates.length) return undefined;
    const handle = setTimeout(() => dispatch(ShoreS.loadThumbnails(narrowedDates)), 400);
    return () => clearTimeout(handle);
  }, [narrowedDates, dispatch]);

  /**
   * @function handleCheckboxChange
   * @description Toggles a single image's checkbox in the selection map.
   * @param {string} key - The image's key, from `getImageKey`.
   * @param {boolean} checked - The new checked state.
   * **Called by:
   * @see {ShoreSPeriodImageChooser} - via each row's checkbox onChange.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleCheckboxChange = (key, checked) => {
    setSelected((prev) => update(prev, { [key]: { $set: checked } }));
  };

  /**
   * @function handleSelectAll
   * @description Applies one checked state to every image in the current list, for the
   * select-all/none control.
   * @param {Array<string>} keys - The image keys to update.
   * @param {boolean} checked - The new checked state.
   * **Called by:
   * @see {ShoreSPeriodImageChooser} - via the header checkbox's onChange.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleSelectAll = (keys, checked) => {
    setSelected((prev) => {
      const changes = keys.reduce((acc, key) => {
        acc[key] = { $set: checked };
        return acc;
      }, {});
      return update(prev, changes);
    });
  };

  /**
   * @function handleFinish
   * @description Saves the checked images and advances to the result. Also backs up the full
   * (pre-selection) narrowed list to sessionStorage before filtering it down — so the user can
   * get back every image found for the chosen period/cloud filter after finalizing a narrower
   * selection and coming back to this step, without redoing the search. Only overwrites the
   * backup when the current list is at least as large, so repeatedly finalizing an already-
   * narrowed list can't shrink it.
   * @returns {void}
   */
  const handleFinish = () => {
    try {
      const existing = JSON.parse(window.sessionStorage.getItem('shoreSFullImageList') || 'null');
      if (!existing || narrowedDates.length >= existing.length) {
        window.sessionStorage.setItem('shoreSFullImageList', JSON.stringify(narrowedDates));
      }
    } catch {
      window.sessionStorage.setItem('shoreSFullImageList', JSON.stringify(narrowedDates));
    }
    const finalSelection = narrowedDates.filter((img) => selected[getImageKey(img)]);
    sessionStorage.setItem('shoreSSelectedDates', JSON.stringify(finalSelection));
  };

  /**
   * @function handleReloadFullList
   * @description Restores the full candidate list backed up by handleFinish (see above), widening
   * the period back out to cover it and resetting the cloud filter to 100% — otherwise the
   * restored list would just get immediately re-narrowed by whatever period/cloud filter is still
   * set locally. Previously checked images stay checked; images that reappear default to checked,
   * same as a fresh narrowed batch.
   * **Called by:
   * @see {ShoreSPeriodImageChooser} - via the "reload full list" button's onClick.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleReloadFullList = () => {
    try {
      const backup = JSON.parse(window.sessionStorage.getItem('shoreSFullImageList') || 'null');
      if (Array.isArray(backup) && backup.length) {
        const sorted = [...backup].sort((a, b) => new Date(a.date) - new Date(b.date));
        const restored = { start: sorted[0].date, end: sorted[sorted.length - 1].date };
        setCloudCoverage(1);
        setPeriod(restored);
        setSearchRange(restored);
        dispatch(ShoreS.loadThumbnails(backup));
      }
    } catch {}
  };

  let fullListBackup = null;
  try {
    fullListBackup = JSON.parse(window.sessionStorage.getItem('shoreSFullImageList') || 'null');
  } catch {
    fullListBackup = null;
  }
  const canReloadFullList = Boolean(fullListBackup?.length > narrowedDates.length);

  const hasDates = availableDates.length > 0;
  const isWaiting = pendingFetch || loading;

  return (
    <Box className={classes.wrapper} display="flex" alignItems="center" flexDirection="column">

      <Box className={classes.searchBox}>
        <Box className={classes.dateInputs}>
          <DateInput
            label={t('forms.acquisition.3.startDate')}
            value={searchRange.start}
            onBlur={(newStart) => setSearchRange((prev) => ({ ...prev, start: toDay(newStart) }))}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            dateFormat={t('formats.date')}
            showCalendarPicker
          />
          <DateInput
            label={t('forms.acquisition.3.endDate')}
            value={searchRange.end}
            onBlur={(newEnd) => setSearchRange((prev) => ({ ...prev, end: toDay(newEnd) }))}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            dateFormat={t('formats.date')}
            showCalendarPicker
          />
        </Box>

        {dateLimits && (
          <Typography className={classes.hint}>
            {t('forms.shoreS.acquisition.dataChooser.dateLimitHint', {
              min: formatLocaleDate(dateLimits.min),
              max: formatLocaleDate(dateLimits.max),
              months: DATE_LIMIT_MONTHS,
            })}
          </Typography>
        )}

        <DateRangeError
          errors={searchErrors}
          limits={dateLimits}
          start={searchRange.start}
          end={searchRange.end}
        />

        <Button variant="contained" color="primary" onClick={handleSearch} disabled={!canSearch || isWaiting}>
          {t('forms.acquisition.3.searchImages')}
        </Button>
      </Box>

      {!hasDates && (
        <Box className={classes.empty}>
          {isWaiting ? (
            <ActivityIndicator textual customSentence={t('forms.core.loading.searchingImages')} />
          ) : error === 'no-roi' ? (
            <Typography variant="body1">{t('forms.shoreS.acquisition.periodChooser.noRoi')}</Typography>
          ) : (
            <Typography variant="body1">{t('forms.shoreS.acquisition.periodChooser.noImages')}</Typography>
          )}
        </Box>
      )}

      {hasDates && (
        <>
          <TimePeriodSelector
            key={visibleDateValues.join('|')}
            dates={visibleDateValues}
            start={clampToVisible(period.start)}
            end={clampToVisible(period.end)}
            onChange={(start, end) => onChange(start, end)}
          />

          <Box className={classes.description}>
            <Typography variant="subtitle1" align="center">
              {t('forms.acquisition.3.period')}: {formatLocaleDate(period.start)} {t('forms.acquisition.3.to')}{' '}
              {formatLocaleDate(period.end)}
            </Typography>
            <Typography variant="subtitle1" align="center">
              {formatDateDiff(period.start, period.end)}, {imageCount}{' '}
              {t(`forms.acquisition.3.imageQuantity.${imageCount === 1 ? 'singular' : 'plural'}`)}
            </Typography>
            {loading && (
              <Typography variant="body2" className={classes.loadingHint}>
                {t('forms.shoreS.acquisition.periodChooser.loadingMore')}
              </Typography>
            )}
          </Box>

          <CloudSelector level={cloudCoverage} onChange={(level) => setCloudCoverage(level)} />

          <Paper className={classes.section} elevation={0} variant="outlined" style={{ width: '100%' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.toolbar}>
                <ImportDatesButton images={narrowedDates} onImport={setSelected} />
                {canReloadFullList && (
                  <Button variant="outlined" color="primary" onClick={handleReloadFullList}>
                    {t('forms.acquisition.4.reloadFullList')}
                  </Button>
                )}
              </Grid>

              <Grid item xs={12}>
                {narrowedDates.length > 0 ? (
                  <ImageTable
                    images={narrowedDates}
                    metadata={metadata}
                    selected={selected}
                    onCheckboxChange={handleCheckboxChange}
                    onSelectAll={handleSelectAll}
                    variant="modern"
                  />
                ) : (
                  <Box className={classes.placeholder}>
                    <Typography variant="body1">
                      {t('forms.shoreS.acquisition.imageSelector.listPlaceholder')}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      <StepperButtons navigate={navigate} onNext={handleFinish} nextTarget={FINALIZE} nextDisabled={!hasDates} />
    </Box>
  );
};

export default ShoreSPeriodImageChooser;
